import { PET_POLICY, utilitiesSentence } from '../property/settings.js'

/**
 * "GOOD TO KNOW" — WHAT A MONTHLY RENTER ASKS BEFORE SHE ASKS ANYTHING ELSE, SET ON EACH HOME.
 * (The Product Owner, 2026-10-08: "每间房子各自设定"; walk-through: utilities, lease, pets, parking,
 * moving out early, check-in — none of them anywhere on a home's page.)
 *
 * Business facts, so they are data on the home, never words in the code (业务规则不能写死在程式里):
 * utilities and pets already live in Property setup and are read from there; the rest is
 * `settings.renterInfo`, typed in the same place. This file only turns them into the sentences a
 * renter reads — and a topic nobody has answered is left off rather than guessed.
 *
 * WHAT NEVER LEAVES: the utilities cap (a promise made on a quote, not on a public page) and the
 * owner's own words about pets (`conditions` — written for an agent, not a renter).
 */

export const PARKING = Object.freeze({ NONE: 'NONE', STREET: 'STREET', DRIVEWAY: 'DRIVEWAY', GARAGE: 'GARAGE', ASSIGNED: 'ASSIGNED' })
export const PARKING_LABEL = Object.freeze({
  NONE: 'No parking', STREET: 'Street parking', DRIVEWAY: 'Driveway', GARAGE: 'Garage', ASSIGNED: 'Assigned spot',
})
export const LEASE = Object.freeze({ FIXED: 'FIXED', FLEXIBLE: 'FLEXIBLE' })
export const LEASE_LABEL = Object.freeze({ FIXED: 'Fixed term', FLEXIBLE: 'Month-to-month after month one' })
export const CHECK_IN = Object.freeze({ SELF: 'SELF', IN_PERSON: 'IN_PERSON' })
export const CHECK_IN_LABEL = Object.freeze({ SELF: 'Self check-in', IN_PERSON: 'We meet you there' })

const BLANK_RENTER_INFO = Object.freeze({
  utilitiesNote: '',
  parking: { kind: null, spots: null, note: '' },
  lease: { kind: null, noticeDays: null, note: '' },
  checkIn: { kind: null, note: '' },
  rules: { noSmoking: false, noParties: false, note: '' },
})

/** The stored value with every part present, whatever an older row held. */
export function renterInfoOf(settings) {
  const r = settings?.renterInfo && typeof settings.renterInfo === 'object' ? settings.renterInfo : {}
  return {
    utilitiesNote: typeof r.utilitiesNote === 'string' ? r.utilitiesNote : '',
    parking: { ...BLANK_RENTER_INFO.parking, ...(r.parking ?? {}) },
    lease: { ...BLANK_RENTER_INFO.lease, ...(r.lease ?? {}) },
    checkIn: { ...BLANK_RENTER_INFO.checkIn, ...(r.checkIn ?? {}) },
    rules: { ...BLANK_RENTER_INFO.rules, ...(r.rules ?? {}) },
  }
}

const clean = (s) => String(s ?? '').trim().replace(/\s+/g, ' ').slice(0, 280)
const dollars = (n) => `$${Math.round(n).toLocaleString('en-US')}`
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/**
 * The rows a renter reads, in the order she asks: money first (utilities), then the lease, then the
 * home. Each is { id, title, lines } — and only the topics somebody has answered.
 */
export function goodToKnowOf(settings) {
  if (!settings) return []
  const info = renterInfoOf(settings)
  const out = []

  const u = settings.utilities ?? {}
  const covers = utilitiesSentence(u.covers ?? [])
  if (covers || u.monthly != null || clean(info.utilitiesNote)) {
    const lines = []
    if (covers) {
      const cap = covers.charAt(0).toUpperCase() + covers.slice(1)
      lines.push(u.monthly != null ? `${cap} — usually about ${dollars(u.monthly)} a month.` : `${cap}.`)
    } else if (u.monthly != null) lines.push(`Usually about ${dollars(u.monthly)} a month.`)
    lines.push('Your quote says whether they are in the rent or billed on top.')
    if (clean(info.utilitiesNote)) lines.push(clean(info.utilitiesNote))
    out.push({ id: 'UTILITIES', title: 'Utilities', lines })
  }

  const l = info.lease
  if (l.kind || l.noticeDays != null || clean(l.note)) {
    const lines = []
    if (l.kind === LEASE.FIXED) lines.push('A lease for the dates you choose.')
    if (l.kind === LEASE.FLEXIBLE) lines.push('A lease for your first month, then month to month.')
    if (Number.isInteger(l.noticeDays) && l.noticeDays > 0) lines.push(`Moving out early: tell us ${plural(l.noticeDays, 'day')} ahead.`)
    if (clean(l.note)) lines.push(clean(l.note))
    out.push({ id: 'LEASE', title: 'Lease & moving out early', lines })
  }

  const p = settings.pets ?? {}
  if (p.policy) {
    const lines = []
    if (p.policy === PET_POLICY.NEVER) lines.push('No pets in this home.')
    if (p.policy === PET_POLICY.CASE_BY_CASE) lines.push('Pets considered — tell us about yours and we’ll ask the owner.')
    if (p.policy === PET_POLICY.WITH_FEE) {
      const allowed = Object.entries(p.limits ?? {}).filter(([, n]) => Number.isInteger(n) && n > 0)
        .map(([a, n]) => plural(n, a === 'DOG' ? 'dog' : a === 'CAT' ? 'cat' : String(a).toLowerCase()))
      lines.push(allowed.length ? `Pets welcome: up to ${allowed.join(' or ')}.` : 'Pets welcome.')
      if (Number.isFinite(p.maxWeightLb)) lines.push(`Up to ${p.maxWeightLb} lb.`)
      if (p.feeAmount != null) lines.push(`Pet fee ${dollars(p.feeAmount)}${p.feeBasis === 'PER_MONTH' ? ' a month' : ' per stay'}.`)
    }
    out.push({ id: 'PETS', title: 'Pets', lines })
  }

  const pk = info.parking
  if (pk.kind || clean(pk.note)) {
    const lines = []
    if (pk.kind === PARKING.NONE) lines.push('No parking comes with this home.')
    else if (pk.kind) {
      const n = Number.isInteger(pk.spots) && pk.spots > 0 ? pk.spots : null
      lines.push(n && pk.kind !== PARKING.STREET ? `${PARKING_LABEL[pk.kind]} — ${plural(n, 'car')}.` : `${PARKING_LABEL[pk.kind]}.`)
    }
    if (clean(pk.note)) lines.push(clean(pk.note))
    out.push({ id: 'PARKING', title: 'Parking', lines })
  }

  const c = info.checkIn
  if (c.kind || clean(c.note)) {
    const lines = []
    if (c.kind === CHECK_IN.SELF) lines.push('Self check-in — we send the details before you arrive.')
    if (c.kind === CHECK_IN.IN_PERSON) lines.push('Someone from our team meets you at the home.')
    if (clean(c.note)) lines.push(clean(c.note))
    out.push({ id: 'CHECK_IN', title: 'Moving in', lines })
  }

  const r = info.rules
  if (r.noSmoking || r.noParties || clean(r.note)) {
    const lines = []
    const no = [r.noSmoking ? 'No smoking' : null, r.noParties ? 'no parties' : null].filter(Boolean)
    if (no.length) lines.push(`${no.join(', ').replace(/^no/, 'No')}.`)
    if (clean(r.note)) lines.push(clean(r.note))
    out.push({ id: 'RULES', title: 'House rules', lines })
  }
  return out
}

/** Problems with what was typed, as sentences — none is required, but what is typed must make sense. */
export function renterInfoProblems(settings) {
  const info = renterInfoOf(settings)
  const out = []
  const n = info.lease.noticeDays
  if (n != null && (!Number.isInteger(n) || n < 0 || n > 365)) out.push('Notice to move out early must be a whole number of days, 0 to 365.')
  const s = info.parking.spots
  if (s != null && (!Number.isInteger(s) || s < 0 || s > 20)) out.push('Parking spots must be a whole number, 0 to 20.')
  return out
}
